import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { LockIcon } from '../components/icons';
import { Banner, ErrorState, Loading, PageHeader, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { DEFAULTABLE_PRIVACY } from '../lib/privacy';
import type { EmailGate, VideoSettings, Visibility } from '../lib/types';
import { qk } from '../queries/keys';
import {
  fetchVideoDefaults,
  setMyVideoDefaults,
  setTeamVideoDefaults,
  type VideoDefaults,
  type VideoSettingKey,
} from '../queries/videoDefaults';
import { SettingsTabs } from './SettingsPage';

/**
 * What every new recording starts with.
 *
 * The share page gives a recording's owner the same settings, starting with
 * who may watch, and answering them on every recording is work nobody should
 * do twice. These are those settings, answered once.
 *
 * Someone with no team sees exactly that and nothing else. An admin or an
 * owner also gets a lock on each row: locked, that answer is the team's, and
 * every member records that way whether they like it or not. A member sees
 * the locked ones as their team's, and cannot change them.
 */

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

const SETTINGS: Array<{ key: VideoSettingKey; title: string; detail: string }> = [
  { key: 'visibility', title: 'Who can watch', detail: 'Who a new recording is open to' },
  { key: 'captionsOn', title: 'Turn on captions', detail: 'Captions show for viewers without them asking' },
  { key: 'playbackRate', title: 'Recommend a playback speed', detail: 'The speed the page opens at' },
  { key: 'emailGate', title: 'Request email to view', detail: 'Viewers who are not signed in give an email before watching' },
  { key: 'commentsEnabled', title: 'Comments', detail: 'Viewers can add comments' },
  { key: 'reactionsEnabled', title: 'Emoji reactions', detail: 'Viewers can react at a moment' },
  { key: 'downloadEnabled', title: 'Download', detail: 'Viewers can download the video' },
  { key: 'analyticsPublic', title: 'Analytics', detail: 'Viewers can see the view count' },
  { key: 'transcriptPublic', title: 'Transcript', detail: 'Viewers get the transcript and captions' },
  { key: 'linksAtEnd', title: 'Links at the end', detail: 'Show the pages you showed only after the video has played' },
];

function Switch({ on, disabled, onChange, label }: { on: boolean; disabled?: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cx(
        'relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-120',
        'disabled:pointer-events-none disabled:opacity-40',
        on ? 'border-go bg-go' : 'border-slate-600 bg-slate-800',
      )}
    >
      <span
        className={cx(
          'absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full shadow-sm transition-transform duration-120',
          on ? 'translate-x-4 bg-white' : 'translate-x-0 bg-chalk-dim',
        )}
      />
    </button>
  );
}

/**
 * The lock, for an admin or an owner. On, this row's value is the team's and
 * everyone records that way; off, it is a suggestion each person may change.
 */
function LockToggle({ locked, disabled, onChange }: { locked: boolean; disabled?: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={locked}
      disabled={disabled}
      onClick={onChange}
      title={locked ? 'The team’s answer. Click to let people choose their own.' : 'Apply this to everyone on the team'}
      aria-label={locked ? 'Stop applying this to everyone on the team' : 'Apply this to everyone on the team'}
      className={cx(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors duration-120',
        'disabled:pointer-events-none disabled:opacity-40',
        locked ? 'bg-signal/15 text-signal' : 'text-chalk-faint hover:bg-slate-800 hover:text-chalk',
      )}
    >
      <LockIcon size={14} />
    </button>
  );
}

function Row({
  title,
  detail,
  locked,
  lockedNote,
  control,
  lock,
}: {
  title: string;
  detail: string;
  locked: boolean;
  lockedNote: string | null;
  control: ReactNode;
  lock: ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 border-b border-hairline py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-body text-chalk">
          {title}
          {locked ? <LockIcon size={12} className="shrink-0 text-signal" /> : null}
        </p>
        <p className="mt-0.5 text-caption text-chalk-faint">{locked && lockedNote ? lockedNote : detail}</p>
      </div>
      {control}
      {lock}
    </div>
  );
}

export function ConfigurationPage() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.videoDefaults(), queryFn: fetchVideoDefaults });

  const refresh = () => client.invalidateQueries({ queryKey: qk.videoDefaults() });

  const mine = useMutation({
    mutationFn: (patch: Partial<Record<VideoSettingKey, unknown>>) => setMyVideoDefaults(patch),
    onSuccess: refresh,
  });
  const team = useMutation({
    mutationFn: (body: { orgId: string; defaults?: Partial<Record<VideoSettingKey, unknown>>; locked?: VideoSettingKey[] }) =>
      setTeamVideoDefaults(body.orgId, { defaults: body.defaults, locked: body.locked }),
    onSuccess: refresh,
  });

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="configuration" />
        <Loading label="Loading your defaults" />
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="configuration" />
        <ErrorState title="That did not load" message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      </>
    );
  }

  const data: VideoDefaults = query.data;
  const canManageTeam = data.team?.canManage ?? false;
  const teamName = data.team?.name ?? 'Your team';
  const busy = mine.isPending || team.isPending;

  /**
   * Changing a row. An admin changing a locked row is changing the team's
   * answer; anyone else is changing their own. Which is why the lock and the
   * value are set in the same call: locking a row means "this value, for
   * everyone", so the value goes with it.
   */
  const change = (key: VideoSettingKey, value: unknown) => {
    const locked = data.locked.includes(key);
    if (locked && canManageTeam && data.team) {
      team.mutate({ orgId: data.team.id, defaults: { [key]: value } });
      return;
    }
    mine.mutate({ [key]: value });
  };

  const toggleLock = (key: VideoSettingKey) => {
    if (!data.team || !canManageTeam) return;
    const locked = data.locked.includes(key);
    team.mutate({
      orgId: data.team.id,
      // Locking carries the value being shown, so the team's answer is the
      // one the admin was looking at when they locked it.
      defaults: locked ? undefined : { [key]: data.effective[key] },
      locked: locked ? data.locked.filter((one) => one !== key) : [...data.locked, key],
    });
  };

  const failure = mine.error ?? team.error;

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
      <SettingsTabs active="configuration" />

      <section className="card p-4 sm:p-5">
        <h2 className="text-h4 text-chalk">New recordings</h2>
        <p className="mt-1 text-caption text-chalk-dim">
          What every recording you make starts with. The same settings sit on each recording’s own page, so any of
          these can still be changed there, one recording at a time. Sharing with a named list of addresses is
          chosen there too, since the list belongs to the recording.
          {canManageTeam ? ` Lock one and it becomes ${teamName}’s answer, applied to everyone on the team.` : ''}
        </p>

        {data.team && !canManageTeam && data.locked.length > 0 ? (
          <div className="mt-4">
            <Banner tone="info" title={`${teamName} sets some of these`}>
              A setting with a lock on it is the team’s, on every recording anyone here makes. The rest are yours.
            </Banner>
          </div>
        ) : null}

        <div className="mt-4">
          {SETTINGS.map((setting) => {
            const locked = data.locked.includes(setting.key);
            // Locked and not an admin: the team has answered it.
            const readOnly = busy || (locked && !canManageTeam);
            const value = data.effective[setting.key];
            const lockedNote = locked ? `${teamName} sets this for everyone` : null;
            // "Your team" and "anyone signed in" are not the same thing, and
            // the difference is the whole point, so the row says which.
            const detail =
              setting.key === 'visibility'
                ? (DEFAULTABLE_PRIVACY.find((option) => option.value === value)?.detail(data.team?.name ?? null) ??
                  setting.detail)
                : setting.detail;

            return (
              <Row
                key={setting.key}
                title={setting.title}
                detail={detail}
                locked={locked}
                lockedNote={lockedNote}
                control={
                  setting.key === 'visibility' ? (
                    <select
                      value={String(value)}
                      disabled={readOnly}
                      aria-label={setting.title}
                      onChange={(event) => change(setting.key, event.target.value as Visibility)}
                      className="h-8 max-w-[14rem] rounded border border-hairline bg-slate-900 px-2 text-caption text-chalk disabled:opacity-40"
                    >
                      {DEFAULTABLE_PRIVACY.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : setting.key === 'playbackRate' ? (
                    <select
                      value={String(value)}
                      disabled={readOnly}
                      aria-label={setting.title}
                      onChange={(event) => change(setting.key, Number(event.target.value))}
                      className="h-8 rounded border border-hairline bg-slate-900 px-2 font-mono text-caption text-chalk disabled:opacity-40"
                    >
                      {SPEEDS.map((speed) => (
                        <option key={speed} value={speed}>
                          {speed}×
                        </option>
                      ))}
                    </select>
                  ) : setting.key === 'emailGate' ? (
                    <select
                      value={String(value)}
                      disabled={readOnly}
                      aria-label={setting.title}
                      onChange={(event) => change(setting.key, event.target.value as EmailGate)}
                      className="h-8 rounded border border-hairline bg-slate-900 px-2 text-caption text-chalk disabled:opacity-40"
                    >
                      <option value="none">No</option>
                      <option value="ask">Ask for email</option>
                      <option value="require">Require email</option>
                    </select>
                  ) : (
                    <Switch
                      on={Boolean(value)}
                      disabled={readOnly}
                      label={setting.title}
                      onChange={() => change(setting.key, !value as VideoSettings[typeof setting.key])}
                    />
                  )
                }
                lock={
                  canManageTeam ? (
                    <LockToggle locked={locked} disabled={busy} onChange={() => toggleLock(setting.key)} />
                  ) : null
                }
              />
            );
          })}
        </div>

        {failure ? (
          <div className="mt-4">
            <Banner tone="danger">{errorMessage(failure)}</Banner>
          </div>
        ) : null}
      </section>
    </>
  );
}
